<template>
  <QuillEditor
    ref="editorRef"
    content-type="html"
    :content="editorContent"
    :toolbar="toolbar"
    :modules="modules"
    placeholder="質問・要望内容を入力..."
    @textChange="onEditorChange"
    @blur="onBlur"
  />
</template>

<script lang="ts">
import './registerQuillImageUploader'
import { defineComponent } from 'vue'
import { QuillEditor } from '@vueup/vue-quill'
import ImageUploader from 'quill-image-uploader'
import 'quill-image-uploader/dist/quill.imageUploader.min.css'
import QuillResizeImage from 'quill-resize-image'
import { $core } from '../../../../CORE/src/front/$core'
import { $fileManager } from '../../../../CORE/src/front/FileManager/FileManager'
import { buildServiceLevelFeedbackImageUrl } from '../buildServiceLevelFeedbackImageUrl'
import 'quill/dist/quill.core.css'
import 'quill/dist/quill.snow.css'

const FILE_PATH_PREFIX = 'yhc/service-level-feedbacks/'

export default defineComponent({
  name: 'YhcServiceLevelFeedbackRichText',
  components: { QuillEditor },
  props: {
    modelValue: {
      type: String,
      default: '',
    },
  },
  emits: ['update:modelValue'],
  data() {
    const initial = this.modelValue || ''
    return {
      /** Quill へ渡す HTML（自コンポーネントからの emit では更新しない） */
      editorContent: initial,
      /** 直近に親へ emit した HTML（親の v-model 往復で Quill を壊さないため） */
      lastEmittedHtml: initial,
      toolbar: [
        [{ header: [2, 3, false] }],
        ['bold', { color: [] }, { background: [] }, 'italic', 'underline'],
        [{ list: 'ordered' }, { list: 'bullet' }],
        ['link', 'image'],
        ['clean'],
      ],
      modules: [
        {
          name: 'imageUploader',
          module: ImageUploader,
          options: {
            upload: async (file: File) => {
              try {
                // filePathPrefix を渡すと upload 内でパスが再生成されるため、
                // 事前 generate したパスと buildUrl 用パスがずれて 403 になる
                const { filePath } = await $fileManager.upload(
                  file,
                  undefined,
                  undefined,
                  FILE_PATH_PREFIX,
                  undefined,
                )
                const url = buildServiceLevelFeedbackImageUrl(filePath)
                if (!url) {
                  throw new Error('Failed to build image URL')
                }
                return url
              } catch (e) {
                $core.$toast.error('画像のアップロードに失敗しました。')
                throw e
              }
            },
          },
        },
        {
          name: 'resize',
          module: QuillResizeImage,
          options: {
            locale: {
              floatLeft: '左詰め',
              floatRight: '右詰め',
              center: '中央寄せ',
              restore: '元に戻す',
              altTip: '縦横サイズ比率を固定するにはaltキーを押しながらドラッグしてください',
              inputTip: '幅指定',
            },
          },
        },
      ],
    }
  },
  watch: {
    modelValue(val: string) {
      const normalized = val || ''
      if (normalized === this.lastEmittedHtml) {
        return
      }
      this.editorContent = normalized
      this.lastEmittedHtml = normalized
    },
  },
  methods: {
    onEditorChange() {
      this.emitUpdate()
    },
    onBlur() {
      this.emitUpdate()
    },
    emitUpdate() {
      this.$nextTick(() => {
        const editorRef = this.$refs.editorRef as {
          getContents?: () => string
        } | null
        const html =
          editorRef
            ?.getContents?.()
            ?.replace(/<span class="ql-cursor">(.+)<\/span>/g, '$1') || ''
        // アップロード中プレースホルダを親へ送ると setHTML で imageBlot が壊れる
        if (html.includes('image-uploading')) {
          return
        }
        const empty = html === '<p><br></p>' || !html.trim()
        const value = empty ? '' : html
        this.lastEmittedHtml = value
        this.$emit('update:modelValue', value)
      })
    },
  },
})
</script>

<style scoped lang="scss">
:deep(.ql-toolbar.ql-snow) {
  border-top-left-radius: var(--bs-border-radius, 0.375rem);
  border-top-right-radius: var(--bs-border-radius, 0.375rem);
  border-color: var(--bs-gray-300, #dee2e6);
}

:deep(.ql-container.ql-snow) {
  border-bottom-left-radius: var(--bs-border-radius, 0.375rem);
  border-bottom-right-radius: var(--bs-border-radius, 0.375rem);
  border-color: var(--bs-gray-300, #dee2e6);
}

:deep(.ql-container) {
  min-height: 160px;
  font-size: 14px;
}

:deep(.ql-editor) {
  min-height: 160px;

  img {
    max-width: 100%;
    height: auto;
  }
}
</style>
